import React, { useState, useEffect } from 'react'
import ImageCarousel from './Carousel'
import CheckBlue from '../../../../media/images/check-blue.svg'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useAuth } from '../../../../app/modules/auth'
import { createClaimDeal } from '../core/_requests'
import Share from '../../../../media/images/share.svg'
import ShareModal from '../../widgets/components/ShareModal'
import { createFounderMarketBookmarks } from '../core/_requests'
import { useThemeMode } from '../../../../_metronic/partials'
import Skeleton from 'react-loading-skeleton'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { DateTime } from 'luxon'
import bookmark from '../../../../media/resources/icons/ic-bookmark.svg'
import bookmarkActive from '../../../../media/resources/icons/ic-bookmark-active.svg'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'

interface FounderMarketDetailDataProps {
  dealData: any
  loading: Boolean
}

const FounderMarketDetailData: React.FC<FounderMarketDetailDataProps> = ({ dealData, loading }) => {
  const { mode } = useThemeMode()
  const { formatMessage } = useIntl()
  const charLimit = 100 // Adjust the limit as needed
  const { companyId, currentUser } = useAuth()
  const [liked, setLiked] = useState(false)
  const { getDateValue } = useDateFormat()
  const [disable, setDisable] = useState<boolean>(false)

  // Splitting the text based on the character limit
  const firstPart = dealData?.description?.substr(0, charLimit)
  const secondPart = dealData?.description?.substr(charLimit)

  // State to manage whether the extra text is visible
  const [isExpanded, setIsExpanded] = useState(false)

  // Function to toggle the expanded state
  const toggleText = () => {
    setIsExpanded(!isExpanded)
  }

  const handleClaimMarketDeal = async () => {
    try {
      const payload = {
        marketDealId: dealData?.marketDealId,
      }
      const {
        data: { success, errors },
      } = await createClaimDeal(payload, companyId!)
      if (success) {
        toast.success(formatMessage({ id: 'Deal claimed successfully' }))
        window.open(dealData?.dealsUrl, '_blank')
        setDisable(false)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.FounderMarketDealClaimed,
            action: 'normal',
            metadata: {
              deal_id: dealData?.marketDealId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              name: dealData?.name || '',
              category: dealData?.category || '',
            },
          },
        })
      } else {
        errors?.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.FounderMarketDealClaimFailed,
            action: 'normal',
            metadata: {
              deal_id: dealData?.marketDealId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              name: dealData?.name || '',
              category: dealData?.category || '',
              error: errors[0],
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDisable(false)
    }
  }

  const createFounderMarketBookmark = async (dealId: any) => {
    try {
      if (companyId) {
        const {
          data: { data: bookmarkRes, errors, success },
        } = await createFounderMarketBookmarks(companyId, dealId)
        if (success) {
          setLiked(!liked)
          console.log(bookmarkRes)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.FounderMarketBookmarked,
              action: 'normal',
              metadata: {
                deal_id: dealId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                name: dealData?.name || '',
                category: dealData?.category || '',
                book_marked: !liked,
              },
            },
          })
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.FounderMarketBookmarkFailed,
              action: 'normal',
              metadata: {
                deal_id: dealId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                error: errors[0],
                name: dealData?.name || '',
                category: dealData?.category || '',
                book_marked: liked,
              },
            },
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  const handleClick = () => {
    createFounderMarketBookmark(dealData?.marketDealId)
  }

  const [isActive, setIsActive] = useState(false)
  const [urlToShare, setUrlToShare] = useState('')
  const handleModal = (url: any) => {
    setIsActive(() => !isActive)
    setUrlToShare(url)
    setIsActive(true)
  }

  useEffect(() => {
    if (dealData) {
      setLiked(dealData?.bookmarkedDeals?.length > 0)
    }
  }, [dealData])

  const [lastUpdated, setLastUpdated] = useState('')

  useEffect(() => {
    if (dealData && dealData.lastUpdatedOn) {
      setLastUpdated(dealData.lastUpdatedOn.slice(0, 10))
    }
  }, [dealData])

  // console.log(dealData,'Deals Data')
  return (
    <>
      {loading ? (
        <div className='row'>
          <div className='col-md-6 h-fc'>
            <Skeleton
              height={300}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              width={140}
              height={80}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          </div>
          <div className='resourcesDetails col-md-6 h-fc'>
            <div className='d-flex justify-content-between'>
              <div>
                <Skeleton
                  height={30}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={30}
                  width={30}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
            <Skeleton
              height={40}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={20}
              width={200}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={30}
              width={200}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={60}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <div className='d-flex gap-2'>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
            <Skeleton
              height={50}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={30}
              count={6}
              width={400}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          </div>
        </div>
      ) : (
        <div className='row'>
          <div className='col-md-6 h-fc'>
            <ImageCarousel images={dealData?.previewImage} />
          </div>

          <div className='resourcesDetails col-md-6 h-fc'>
            <div className='d-flex justify-content-between'>
              <div className='d-flex'>
                <div className={`px-4 py-2 category` + (mode === `dark` ? `-dark` : ``)}>
                  {dealData?.category}
                </div>
                <button className='ms-4 text-primary bg-transparent border-0 font-size-13'>{formatMessage({ id: 'Edit' })}</button>
              </div>
              <div className='d-flex align-items-center'>
                <button className='btn m-0 p-0 d-block' onClick={handleClick}>
                  <img
                    src={liked ? bookmarkActive : bookmark}
                    alt={
                      liked
                        ? formatMessage({ id: 'Checkmark Active' })
                        : formatMessage({ id: 'Checkmark' })
                    }
                    className='like align-self-center'
                    style={{ height: '24px', width: '24px', marginRight: '14px' }}
                  />
                </button>
                <button
                  className='btn m-0 p-0 d-block'
                  onClick={() => handleModal(window.location.href)}
                >
                  <img
                    src={Share}
                    alt='share'
                    className='share align-self-center'
                    style={{ height: '24px', width: '24px', marginRight: '14px' }}
                  />
                </button>
              </div>
            </div>
            {isActive && (
              <ShareModal isActive={isActive} setIsActive={setIsActive} url={urlToShare} />
            )}
            <div className='ResourcesTitle mt-2'>
              <div className='d-flex'>
                <h1>{dealData?.name}</h1>
                <button className='ms-4 text-primary bg-transparent border-0 font-size-13'>{formatMessage({ id: 'Edit' })}</button>
              </div>
            </div>

            <div>
              <p
                className='px-3 py-2 m-0 font-size-12'
                style={{
                  display: 'inline-block',
                  borderRadius: '4px',
                  background: 'rgba(71, 118, 230, 0.10)',
                  color: '#4776E6',
                }}
              >
                {formatMessage({ id: 'Last Updated:' })}
                <span>
                  &nbsp;
                  {getDateValue(lastUpdated?.toString())
                    ? getDateValue(lastUpdated?.toString())
                    : DateTime.fromISO(lastUpdated?.toString()).toLocaleString(DateTime.DATE_MED)}
                </span>
              </p>
            </div>
            <div>
              <button
                className='btn btn-primary mt-4'
                onClick={() => {
                  setDisable(true)
                  handleClaimMarketDeal()
                }}
                disabled={disable}
              >
                {disable ? formatMessage({ id: 'Please Wait...' }) : formatMessage({ id: 'Claim Now' })}
              </button>
              {/* <img src={Check} alt='' /> */}
            </div>

            <div className='resourcesTags my-4'>
              <div className='d-flex gap-2'>
                {dealData?.tags?.map((tag: any, index: any) => {
                  return (
                    <p key={index} className='tag'>
                      #{tag.tagName}
                    </p>
                  )
                })}
                <button className='ms-4 text-primary bg-transparent border-0 font-size-13'>{formatMessage({ id: 'Edit' })}</button>
              </div>
            </div>

            <div className='resourceDesc'>
              <p>
                {firstPart}
                {secondPart && isExpanded && <span>{secondPart}</span>}&nbsp;
                {secondPart && (
                  <button className='readmore' onClick={toggleText}>
                    {isExpanded
                      ? formatMessage({ id: 'Show Less' })
                      : formatMessage({ id: 'Read More' })}
                  </button>
                )}
                <button className='ms-4 text-primary bg-transparent border-0 font-size-13'>{formatMessage({ id: 'Edit' })}</button>
              </p>
            </div>

            <div className='resourceBenefits'>
              <h3>{formatMessage({ id: 'Benefits' })}</h3>
              <div className='benefitsList'>
                {dealData?.benefits?.map((val: any, index: any) => {
                  return (
                    <div className='d-flex'>
                      <div className='benefit' key={index}>
                        <img src={CheckBlue} alt='' />
                        <p>{val}</p>
                      </div>
                      <button className='ms-4 text-primary bg-transparent border-0 font-size-13'>{formatMessage({ id: 'Edit' })}</button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FounderMarketDetailData
